import axios from 'axios';
import { ITelegramMessengerRepository } from '../../application/ports/ITelegramMessengerRepository';
import {
  TelegramCallbackAnswerEntity,
  TelegramDialogEntity,
  TelegramDialogKind,
  TelegramDialogMessageEntity,
  TelegramInboxSettingsEntity,
  TelegramJoinResultEntity,
  TelegramJoinStatus,
  TelegramJoinTargetEntity,
  TelegramMessageButtonEntity,
  TelegramMessageButtonKind,
  TelegramMessagesPage,
  TelegramPeerCardEntity,
  TelegramPeerType,
} from '../../domain/entities/TelegramMessenger';
import { advertizerApiHost } from '../../constants/envs';
import { AuthTokenProvider } from '../auth/AuthTokenProvider';

const BASE_PATH = '/api/admin/telegram_accounts';

interface TelegramMessageButtonDTO {
  kind: TelegramMessageButtonKind;
  text: string;
  data?: string | null;
  url?: string | null;
}

interface TelegramDialogMessageDTO {
  id: string;
  out: boolean;
  text: string;
  mediaType?: string | null;
  sentAt: string;
  senderName?: string | null;
  buttons?: TelegramMessageButtonDTO[][] | null;
}

interface TelegramDialogDTO {
  peerType?: TelegramPeerType | null;
  peerId: string;
  accessHash: string;
  kind?: TelegramDialogKind | null;
  title: string;
  username?: string | null;
  unreadCount?: number;
  canSend?: boolean | null;
  lastMessage?: TelegramDialogMessageDTO | null;
}

interface TelegramInboxSettingsDTO {
  enabled: boolean;
  chatId?: string | null;
}

interface TelegramPeerCardDTO {
  peerType: TelegramPeerType;
  peerId: string;
  accessHash: string;
  kind: TelegramDialogKind;
  title: string;
  username?: string | null;
  joined: boolean;
}

interface TelegramJoinResponseDTO {
  status: TelegramJoinStatus;
  peer?: TelegramPeerCardDTO | null;
}

interface TelegramCallbackAnswerDTO {
  message?: string | null;
  url?: string | null;
  alert?: boolean;
}

const toButtonEntity = (dto: TelegramMessageButtonDTO): TelegramMessageButtonEntity => ({
  kind: dto.kind,
  text: dto.text,
  data: dto.data ?? undefined,
  url: dto.url ?? undefined,
});

const toMessageEntity = (dto: TelegramDialogMessageDTO): TelegramDialogMessageEntity => ({
  id: dto.id,
  out: dto.out,
  text: dto.text ?? '',
  mediaType: dto.mediaType ?? undefined,
  sentAt: dto.sentAt,
  senderName: dto.senderName ?? undefined,
  buttons: dto.buttons?.length ? dto.buttons.map((row) => row.map(toButtonEntity)) : undefined,
});

const toDialogEntity = (dto: TelegramDialogDTO): TelegramDialogEntity => ({
  peerType: dto.peerType ?? 'user',
  peerId: dto.peerId,
  accessHash: dto.accessHash,
  kind: dto.kind ?? 'user',
  title: dto.title,
  username: dto.username ?? undefined,
  unreadCount: dto.unreadCount ?? 0,
  canSend: dto.canSend ?? true,
  lastMessage: dto.lastMessage ? toMessageEntity(dto.lastMessage) : undefined,
});

const toSettingsEntity = (dto: TelegramInboxSettingsDTO): TelegramInboxSettingsEntity => ({
  enabled: dto.enabled,
  chatId: dto.chatId ?? undefined,
});

const toPeerCardEntity = (dto: TelegramPeerCardDTO): TelegramPeerCardEntity => ({
  peerType: dto.peerType,
  peerId: dto.peerId,
  accessHash: dto.accessHash,
  kind: dto.kind,
  title: dto.title,
  username: dto.username ?? undefined,
  joined: dto.joined,
});

const toJoinResultEntity = (dto: TelegramJoinResponseDTO): TelegramJoinResultEntity => ({
  status: dto.status,
  peer: dto.peer ? toPeerCardEntity(dto.peer) : undefined,
});

const toCallbackAnswerEntity = (dto: TelegramCallbackAnswerDTO): TelegramCallbackAnswerEntity => ({
  message: dto.message || undefined,
  url: dto.url || undefined,
  alert: dto.alert ?? false,
});

const toJoinRequest = (target: TelegramJoinTargetEntity) =>
  'link' in target
    ? { link: target.link }
    : { peerType: target.peer.peerType, peerId: target.peer.peerId, accessHash: target.peer.accessHash };

export class TelegramMessengerAxiosRepository implements ITelegramMessengerRepository {
  constructor(private auth: AuthTokenProvider) {}

  private async authHeaders() {
    const token = await this.auth.getToken();
    if (!token) throw new Error('Unauthenticated');
    return { Authorization: `Bearer ${token}` };
  }

  async listDialogs(accountUuid: string): Promise<TelegramDialogEntity[]> {
    const headers = await this.authHeaders();
    const res = await axios.get(`${advertizerApiHost}${BASE_PATH}/${accountUuid}/dialogs`, { headers });
    return (res.data.dialogs ?? []).map(toDialogEntity);
  }

  async listMessages(accountUuid: string, dialog: TelegramDialogEntity, page?: TelegramMessagesPage): Promise<TelegramDialogMessageEntity[]> {
    const headers = await this.authHeaders();
    const params: Record<string, string> = { accessHash: dialog.accessHash, peerType: dialog.peerType };
    if (page?.offsetId) params.offsetId = page.offsetId;
    if (page?.minId) params.minId = page.minId;
    const res = await axios.get(`${advertizerApiHost}${BASE_PATH}/${accountUuid}/dialogs/${dialog.peerId}/messages`, { headers, params });
    return (res.data.messages ?? []).map(toMessageEntity);
  }

  async sendMessage(accountUuid: string, dialog: TelegramDialogEntity, text: string): Promise<void> {
    const headers = await this.authHeaders();
    await axios.post(`${advertizerApiHost}${BASE_PATH}/${accountUuid}/dialogs/${dialog.peerId}/messages`, { accessHash: dialog.accessHash, peerType: dialog.peerType, text }, { headers });
  }

  async getInboxSettings(accountUuid: string): Promise<TelegramInboxSettingsEntity> {
    const headers = await this.authHeaders();
    const res = await axios.get(`${advertizerApiHost}${BASE_PATH}/${accountUuid}/inbox_notifications`, { headers });
    return toSettingsEntity(res.data);
  }

  async updateInboxSettings(accountUuid: string, settings: TelegramInboxSettingsEntity): Promise<TelegramInboxSettingsEntity> {
    const headers = await this.authHeaders();
    const res = await axios.put(`${advertizerApiHost}${BASE_PATH}/${accountUuid}/inbox_notifications`, settings, { headers });
    return toSettingsEntity(res.data);
  }

  async searchPeers(accountUuid: string, query: string): Promise<TelegramPeerCardEntity[]> {
    const headers = await this.authHeaders();
    const res = await axios.get(`${advertizerApiHost}${BASE_PATH}/${accountUuid}/search`, { headers, params: { q: query } });
    return (res.data.results ?? []).map(toPeerCardEntity);
  }

  async join(accountUuid: string, target: TelegramJoinTargetEntity): Promise<TelegramJoinResultEntity> {
    const headers = await this.authHeaders();
    const res = await axios.post(`${advertizerApiHost}${BASE_PATH}/${accountUuid}/join`, toJoinRequest(target), { headers });
    return toJoinResultEntity(res.data);
  }

  async pressButton(accountUuid: string, dialog: TelegramDialogEntity, messageId: string, data: string): Promise<TelegramCallbackAnswerEntity> {
    const headers = await this.authHeaders();
    const res = await axios.post(
      `${advertizerApiHost}${BASE_PATH}/${accountUuid}/dialogs/${dialog.peerId}/messages/${messageId}/callback`,
      { accessHash: dialog.accessHash, peerType: dialog.peerType, data },
      { headers },
    );
    return toCallbackAnswerEntity(res.data);
  }
}
